// src/pages/tickets/TicketsPage.tsx
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui';
import { QDataTable } from '@/components/tables';
import { Plus, Clock, MessageSquare, Paperclip } from 'lucide-react';
import { formatRelativeTime, formatDate } from '@/lib/utils';
import { useTickets } from '@/hooks/queries/useTickets';
import { useScreenState } from '@/hooks/useScreenState';
import { useIsMobile } from '@/hooks/useIsMobile';
import { buildTableProps } from '@/lib/screen-utils';
import { ticketScreenDef } from '@/screen-definitions';
import { TicketFormModal } from './components';
import { TicketsMobile } from './components/mobile/TicketsMobile';
import type { Ticket, TicketFiltros, EstadoTicket, TipoTicket, PrioridadTicket } from '@/types';

export function TicketsPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const screenState = useScreenState(ticketScreenDef);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Build entity-specific query params from screen state
  const queryFiltros = useMemo<TicketFiltros>(() => {
    const estados = screenState.quickFilterValues.estado;
    return {
      search: screenState.search || undefined,
      estado: estados?.length === 1 ? estados[0] as EstadoTicket : undefined,
      tipo: screenState.filterValues.tipo as TipoTicket | undefined,
      prioridad: screenState.filterValues.prioridad as PrioridadTicket | undefined,
    };
  }, [screenState.search, screenState.quickFilterValues, screenState.filterValues]);

  const query = useTickets(queryFiltros);

  // ── Mobile View ──
  if (isMobile) {
    return (
      <>
        <TicketsMobile
          tickets={(query.data ?? []) as Ticket[]}
          isLoading={query.isLoading}
        />
        <TicketFormModal open={isModalOpen} onClose={() => setIsModalOpen(false)} />
      </>
    );
  }

  // ── Desktop View ──
  const tableProps = buildTableProps(ticketScreenDef, screenState, query, {
    getId: (t: Ticket) => t.id,
    onRowClick: (t: Ticket) => navigate(`/tickets/${t.id}`),
    customRenderers: {
      created_at: (row: Ticket) => (
        <div>
          <div className="text-sm text-muted-foreground">{formatRelativeTime(row.created_at)}</div>
          {row.fecha_limite && (
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatDate(row.fecha_limite)}
            </div>
          )}
        </div>
      ),
      _counters: (row: Ticket) => (
        <div className="flex items-center gap-2">
          {(row.comentarios_count ?? 0) > 0 && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <MessageSquare className="h-3 w-3" />
              {row.comentarios_count}
            </span>
          )}
          {(row.adjuntos_count ?? 0) > 0 && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Paperclip className="h-3 w-3" />
              {row.adjuntos_count}
            </span>
          )}
        </div>
      ),
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Tickets</h1>
          <p className="text-muted-foreground">Gestiona los tickets de soporte</p>
        </div>
      </div>

      <QDataTable
        {...tableProps}
        headerActions={
          <Button onClick={() => setIsModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Ticket
          </Button>
        }
      />

      <TicketFormModal open={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
